<template>
  <div class="analytics-dashboard" :class="{ 'nav-collapsed': isNavCollapsed }">
    <Navigation @nav-toggle="handleNavToggle" />

    <!-- Main Content -->
    <div class="dashboard-content">
      <div class="page-container">
        <!-- Page Header -->
        <div class="page-header">
          <div class="header-row">
            <div class="title-block">
              <h1 class="page-title">{{ dashboardPageTitle }}</h1>
              <p class="page-subtitle">{{ dashboardSubtitle }}</p>
            </div>
            <div class="header-actions">
              <div class="period-selector">
                <button class="period-btn" @click="previousPeriod">
                  <i class="bx bx-chevron-left"></i>
                </button>
                <span class="current-period">{{ currentPeriod }}</span>
                <button class="period-btn" @click="nextPeriod">
                  <i class="bx bx-chevron-right"></i>
                </button>
              </div>
              <button class="btn-refresh" @click="refreshDashboard" :disabled="refreshing">
                <i class="bx bx-refresh" :class="{ 'spin': refreshing }"></i>
                {{ refreshing ? 'Refreshing...' : 'Refresh' }}
              </button>
              <button class="btn-refresh btn-logout" @click="logoutDashboard">
                <i class="bx bx-log-out"></i> Logout
              </button>
            </div>
          </div>

          <div class="welcome-banner">
            <div class="welcome-text">
              <h2 class="welcome-title">Welcome back, {{ displayName }}</h2>
              <p class="welcome-date">{{ formattedCurrentDate }}</p>
            </div>
          </div>
        </div>

        <!-- Key Metrics -->
        <div class="metrics-grid">
          <div class="metric-card" @click="goToEmployeeRegister">
            <div class="metric-icon">
              <i class="bx bx-group"></i>
            </div>
            <div class="metric-content">
              <span class="metric-label">Total Establishment</span>
              <span class="metric-value">{{ formatNumber(metrics.totalEmployees) }}</span>
              <span class="metric-trend" v-if="metrics.newEmployeeRate > 0">
                <i class="bx bx-up-arrow-alt"></i> {{ metrics.newEmployeeRate }}%
              </span>
            </div>
          </div>

          <div class="metric-card" @click="goToHRActions">
            <div class="metric-icon">
              <i class="bx bx-user-plus"></i>
            </div>
            <div class="metric-content">
              <span class="metric-label">Added This Month</span>
              <span class="metric-value">{{ formatNumber(metrics.employeesAddedThisMonth) }}</span>
              <span class="metric-badge">New Hires</span>
            </div>
          </div>

          <div class="metric-card" @click="goToAttendance">
            <div class="metric-icon">
              <i class="bx bx-user-x"></i>
            </div>
            <div class="metric-content">
              <span class="metric-label">Absent (Unauthorized)</span>
              <span class="metric-value">{{ formatNumber(metrics.absentWithoutPermission) }}</span>
              <span class="metric-trend negative">
                <i class="bx bx-error"></i> {{ metrics.absentWithoutPermissionRate }}%
              </span>
            </div>
          </div>

          <div class="metric-card" @click="goToLeaveManagement">
            <div class="metric-icon">
              <i class="bx bx-user-check"></i>
            </div>
            <div class="metric-content">
              <span class="metric-label">Absent (Authorized)</span>
              <span class="metric-value">{{ formatNumber(metrics.absentWithPermission) }}</span>
              <span class="metric-badge">Approved Leave</span>
            </div>
          </div>
        </div>

        <!-- Charts Section -->
        <div class="charts-grid">
          <!-- Attendance Summary -->
          <div class="chart-card">
            <div class="card-header">
              <h3 class="card-title">
                <i class="bx bx-calendar"></i> Attendance Summary
              </h3>
              <div class="card-controls">
                <div class="year-control">
                  <button class="control-btn" @click="decreaseAttendanceYear">
                    <i class="bx bx-chevron-left"></i>
                  </button>
                  <span class="current-year">{{ attendanceSummaryYear }}</span>
                  <button class="control-btn" @click="increaseAttendanceYear">
                    <i class="bx bx-chevron-right"></i>
                  </button>
                </div>
                <button class="control-btn" @click="exportChart('attendance')" title="Export">
                  <i class="bx bx-download"></i>
                </button>
              </div>
            </div>
            <div class="card-body">
              <div id="AttendanceContainer" ref="attendanceChart"></div>
              <div v-if="!attendanceData" class="chart-placeholder">
                <div class="spinner sm"></div>
                <p>Loading data...</p>
              </div>
            </div>
          </div>

          <!-- Today's Attendance -->
          <div class="chart-card">
            <div class="card-header">
              <h3 class="card-title">
                <i class="bx bx-time"></i> Today's Attendance
              </h3>
              <div class="card-controls">
                <span class="date-badge">{{ formattedCurrentDate }}</span>
                <button class="control-btn" @click="refreshTodayAttendance" title="Refresh">
                  <i class="bx bx-refresh"></i>
                </button>
              </div>
            </div>
            <div class="card-body">
              <div id="TodayAttendanceContainer" ref="todayAttendanceChart"></div>
              <div v-if="!todayAttendanceData" class="chart-placeholder">
                <div class="spinner sm"></div>
                <p>Loading data...</p>
              </div>
              <div class="stats-row" v-if="todayAttendanceData">
                <div class="stat-item">
                  <span class="stat-label">Present</span>
                  <span class="stat-value" style="color: #2195d3">{{ todayAttendanceStats.present || 0 }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">Absent</span>
                  <span class="stat-value" style="color: #dc2626">{{ todayAttendanceStats.absent || 0 }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">Late</span>
                  <span class="stat-value" style="color: #f59e0b">{{ todayAttendanceStats.late || 0 }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Staff Distribution (hidden for department-scoped HR; attendance summary stays visible above) -->
          <div v-if="showStaffDistribution" class="chart-card">
            <div class="card-header">
              <h3 class="card-title">
                <i class="bx bx-refresh"></i> Staff Distribution per Department
              </h3>
              <div class="card-controls">
                <span class="period-badge">{{ currentMonth }} {{ currentYear }}</span>
                <button class="control-btn" @click="exportChart('turnover')" title="Export">
                  <i class="bx bx-download"></i>
                </button>
              </div>
            </div>
            <div class="card-body">
              <div id="NewContainer" ref="turnoverChart"></div>
              <div v-if="!staffTurnoverData" class="chart-placeholder">
                <div class="spinner sm"></div>
                <p>Loading data...</p>
              </div>
            </div>
          </div>

          <!-- Nature of Exits -->
          <div class="chart-card">
            <div class="card-header">
              <h3 class="card-title">
                <i class="bx bx-exit"></i> Nature of Exits
              </h3>
            </div>
            <div class="card-body">
              <div id="ExitChart" ref="exitChart"></div>
              <div v-if="!natureOfExitData" class="chart-placeholder">
                <div class="spinner sm"></div>
                <p>Loading data...</p>
              </div>
              <div class="stats-grid" v-if="natureOfExitData">
                <div class="stat-row-item" v-for="item in exitSummary" :key="item.name">
                  <span class="stat-label">{{ item.name }}</span>
                  <span class="stat-value">{{ item.value }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Employee Composition (Male, Female, PWDs) -->
          <div class="chart-card">
            <div class="card-header">
              <h3 class="card-title">
                <i class="bx bx-pie-chart-alt"></i> Employee Composition
              </h3>
            </div>
            <div class="card-body">
              <div id="compositionChart" ref="compositionChart"></div>
              <div v-if="!employeeCompositionData" class="chart-placeholder">
                <div class="spinner sm"></div>
                <p>Loading data...</p>
              </div>
            </div>
          </div>

          <!-- Job Groups -->
          <div class="chart-card">
            <div class="card-header">
              <h3 class="card-title">
                <i class="bx bx-group"></i> Job Groups
              </h3>
            </div>
            <div class="card-body">
              <div id="jobGroupsChart" ref="jobGroupsChart"></div>
              <div v-if="!jobGroupsData" class="chart-placeholder">
                <div class="spinner sm"></div>
                <p>Loading data...</p>
              </div>
            </div>
          </div>

          <!-- Wage Bill per Department (hidden for Departmental HR) -->
          <div class="chart-card" v-if="showWageBillPerDepartment">
            <div class="card-header">
              <h3 class="card-title">
                <i class="bx bx-money"></i> Wage Bill per Department
              </h3>
              <p v-if="wageBillByDepartmentData && wageBillTotal != null" class="mb-0 mt-1 text-muted small">
                Total: <strong class="text-body">{{ formatWageBillKes(wageBillTotal) }}</strong> KES
              </p>
            </div>
            <div class="card-body">
              <div id="wageBillChart" ref="wageBillChart"></div>
              <div v-if="!wageBillByDepartmentData" class="chart-placeholder">
                <div class="spinner sm"></div>
                <p>Loading data...</p>
              </div>
            </div>
          </div>

          <!-- Gender Distribution -->
          <div class="chart-card">
            <div class="card-header">
              <h3 class="card-title">
                <i class="bx bx-male-female"></i> Gender Distribution
              </h3>
            </div>
            <div class="card-body">
              <div id="GenderContainer" ref="genderChart"></div>
              <div v-if="!genderComparisonData" class="chart-placeholder">
                <div class="spinner sm"></div>
                <p>Loading data...</p>
              </div>
              <div class="stats-row" v-if="genderComparisonData">
                <div class="stat-item">
                  <span class="stat-label">Male</span>
                  <span class="stat-value">{{ genderStats.male || 0 }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">Female</span>
                  <span class="stat-value">{{ genderStats.female || 0 }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Disability Distribution -->
          <div class="chart-card">
            <div class="card-header">
              <h3 class="card-title">
                <i class="bx bx-accessibility"></i> Employees with Disabilities
              </h3>
            </div>
            <div class="card-body">
              <div id="disabilityChart" ref="disabilityChart"></div>
              <div v-if="!disabilityDistribution" class="chart-placeholder">
                <div class="spinner sm"></div>
                <p>Loading data...</p>
              </div>
              <div class="stats-grid" v-if="disabilityDistribution">
                <div class="stat-row-item">
                  <span class="stat-label">With Disabilities</span>
                  <span class="stat-value">{{ disabilityStats.withDisabilities || 0 }}</span>
                </div>
                <div class="stat-row-item">
                  <span class="stat-label">Percentage</span>
                  <span class="stat-value">{{ disabilityStats.percentage || 0 }}%</span>
                </div>
              </div>
            </div>
          </div>

          <div class="chart-card">
            <div class="card-header">
              <h3 class="card-title">
                <i class="bx bx-network-chart"></i> {{ ethnicityTitle }}
              </h3>
            </div>
            <div class="card-body">
              <div class="table-responsive" style="max-height: 260px; overflow: auto;">
                <table class="table table-sm table-bordered mb-0">
                  <thead class="table-light"><tr><th>Ethnicity</th><th>Count</th></tr></thead>
                  <tbody>
                    <tr v-for="(e, i) in ethnicityDistribution" :key="'eth-' + i">
                      <td>{{ e.name }}</td>
                      <td>{{ e.count }}</td>
                    </tr>
                    <tr v-if="!ethnicityDistribution.length"><td colspan="2" class="text-muted">No ethnicity data found.</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import Navigation from "@/components/Navigation.vue";
import Highcharts from 'highcharts';
import 'highcharts/modules/drilldown';
import 'highcharts/modules/exporting';
import 'highcharts/modules/export-data';
import 'highcharts/modules/accessibility';
import { authUrl, execute } from "@/api";

Highcharts.setOptions({
  credits: { enabled: false },
  lang: { thousandsSep: ',', decimalPoint: '.' },
  chart: { backgroundColor: 'transparent', style: { fontFamily: 'inherit' } },
  colors: ['#2195d3', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#64748b']
});

export default {
  name: "Dashboard",
  components: { Navigation },
  data() {
    return {
      isNavCollapsed: false,
      refreshing: false,
      showHelpModal: false,
      
      currentYear: new Date().getFullYear(),
      currentMonth: new Date().toLocaleString('default', { month: 'long' }),
      formattedCurrentDate: new Date().toLocaleDateString('en-US', { 
        year: 'numeric', month: 'long', day: 'numeric' 
      }),
      currentPeriod: 'This Month',
      
      metrics: {
        totalEmployees: 0,
        employeesAddedThisMonth: 0,
        absentWithoutPermission: 0,
        absentWithPermission: 0,
        absentWithoutPermissionRate: 0,
        absentWithPermissionRate: 0,
        newEmployeeRate: 0,
        attendanceRate: 0,
        turnoverRate: 0
      },
      
      attendanceData: null,
      attendanceSummaryYear: new Date().getFullYear(),
      todayAttendanceData: null,
      staffTurnoverData: null,
      natureOfExitData: null,
      genderComparisonData: null,
      disabilityDistribution: null,
      employeeCompositionData: null,
      jobGroupsData: null,
      wageBillByDepartmentData: null,
      
      todayAttendanceStats: { present: 0, absent: 0, late: 0, total: 0 },
      genderStats: { male: 0, female: 0, other: 0 },
      disabilityStats: { withDisabilities: 0, total: 0, percentage: 0 },
      ethnicityDistribution: [],
      exitSummary: [],
      systemUptime: '99.8%',
      chartInstances: {
        attendance: null, todayAttendance: null, turnover: null,
        exit: null, composition: null, jobGroups: null, wageBill: null,
        gender: null, disability: null
      },
      dashboardScope: { type: '', departmentId: '', departmentName: '', departmentScoped: false }
    }
  },
  computed: {
    displayName() {
      try {
        const userInfoRaw = sessionStorage.getItem("userInfo");
        if (userInfoRaw) {
          const user = JSON.parse(userInfoRaw);
          return user?.names || sessionStorage.getItem("sessionNames") || "Admin";
        }
        return sessionStorage.getItem("sessionNames") || "Admin";
      } catch {
        return "Admin";
      }
    },
    wageBillTotal() {
      const d = this.wageBillByDepartmentData;
      if (!d) return null;
      if (d.total != null && d.total !== "") {
        const n = Number(d.total);
        return Number.isFinite(n) ? n : null;
      }
      const series = d.chartData?.series;
      const data = Array.isArray(series) && series[0] && Array.isArray(series[0].data) ? series[0].data : [];
      if (!data.length) return 0;
      const sum = data.reduce((acc, v) => acc + (Number(v) || 0), 0);
      return sum;
    },
    /** County-wide UI: API scope first; session role check via methods in loadDashboardData (not computed — sessionStorage is not reactive). */
    isCountyWideDashboard() {
      const t = String(this.dashboardScope?.type || "").toLowerCase();
      return t === "governor" || t === "hr_admin";
    },
    dashboardPageTitle() {
      if (String(this.dashboardScope?.type || "").toLowerCase() === "governor") {
        return "Governor Dashboard";
      }
      return "Dashboard";
    },
    dashboardSubtitle() {
      const dept = this.dashboardScope?.departmentName || '';
      if (this.isDepartmentalScope && dept) {
        return `${dept} human resource operations overview`;
      }
      return 'Homa Bay County human resource operations overview';
    },
    rbacScope() {
      try {
        const raw = sessionStorage.getItem("rbac_context");
        if (!raw) return "";
        const ctx = JSON.parse(raw);
        return String(ctx?.data_scope || "").toUpperCase();
      } catch (_) {
        return "";
      }
    },
    rbacDepartmentId() {
      try {
        const raw = sessionStorage.getItem("rbac_context");
        if (!raw) return "";
        const ctx = JSON.parse(raw);
        return String(ctx?.department_id || "").trim();
      } catch (_) {
        return "";
      }
    },
    selectedRoleNames() {
      try {
        const rolesRaw = sessionStorage.getItem("selectedRoles");
        const roles = rolesRaw ? JSON.parse(rolesRaw) : [];
        if (!Array.isArray(roles)) return [];
        return roles
          .map((r) => String(r?.role_name || r?.roleName || "").trim().toUpperCase())
          .filter(Boolean);
      } catch (_) {
        return [];
      }
    },
    isDepartmentalHrRole() {
      return this.selectedRoleNames.some((name) =>
        name.includes("DEPARTMENTAL HR") || name === "DEPARTMENTAL_HR"
      );
    },
    isDepartmentalScope() {
      if (this.isCountyWideDashboard) return false;
      if (this.dashboardScope?.departmentScoped === true) return true;
      if (this.rbacScope === 'DEPARTMENT' || this.rbacScope === 'SELF') return true;
      const type = String(this.dashboardScope?.type || '').toLowerCase();
      return type === 'department' || this.isDepartmentalHrRole;
    },
    effectiveDepartmentId() {
      if (this.isCountyWideDashboard) return "";
      if (this.rbacDepartmentId) return this.rbacDepartmentId;
      const fromScope = String(this.dashboardScope?.departmentId || "").trim();
      if (fromScope) return fromScope;
      try {
        const raw = sessionStorage.getItem("userInfo");
        const user = raw ? JSON.parse(raw) : {};
        return String(user?.departmentID || user?.departmentId || "").trim();
      } catch (_) {
        return "";
      }
    },
    showStaffDistribution() {
      if (this.dashboardScope?.departmentScoped === true) return false;
      return !this.isDepartmentalScope;
    },
    ethnicityTitle() {
      let dept = this.dashboardScope?.departmentName || '';
      if (!dept) {
        try {
          const raw = sessionStorage.getItem("userInfo");
          const user = raw ? JSON.parse(raw) : {};
          dept = String(user?.departmentName || "").trim();
        } catch (_) {}
      }
      if (this.isDepartmentalScope && dept) {
        return `${dept} Ethnicity Distribution`;
      }
      return 'Ethnicity Distribution';
    },
    showWageBillPerDepartment() {
      if (this.dashboardScope?.hideWageBillByDepartment) return false;
      if (this.isDepartmentalHrRole) return false;
      return true;
    }
  },
  mounted() {
    this.initializeDashboard();
    this.setupAutoRefresh();
  },
  beforeDestroy() {
    this.cleanupCharts();
    if (this.autoRefreshInterval) clearInterval(this.autoRefreshInterval);
  },
  methods: {
    handleNavToggle(isCollapsed) { this.isNavCollapsed = isCollapsed; },
    dashRoleRowLabel(r) {
      if (!r || typeof r !== "object") return "";
      return String(
        r.role_name ?? r.roleName ?? r.Role_Name ?? r.ROLE_NAME ?? r.name ?? ""
      ).trim();
    },
    dashStringLooksLikeGovernor(s) {
      const t = String(s || "").trim();
      if (!t) return false;
      const u = t.toUpperCase();
      const norm = u.replace(/[^A-Z0-9]/g, "");
      if (norm === "GOVERNOR" || u === "GOVERNOR") return true;
      return /\bGOVERNOR\b/i.test(t);
    },
    dashSessionIsGovernor() {
      try {
        const ctxRaw = sessionStorage.getItem("rbac_context");
        if (ctxRaw) {
          const ctx = JSON.parse(ctxRaw);
          if (this.dashStringLooksLikeGovernor(ctx?.user_category)) return true;
          const names = ctx?.role_names;
          if (Array.isArray(names) && names.some((n) => this.dashStringLooksLikeGovernor(n))) return true;
        }
      } catch (_) {}
      try {
        const rbacStr = sessionStorage.getItem("rbac_permissions");
        if (rbacStr) {
          const perms = JSON.parse(rbacStr);
          if (Array.isArray(perms) && perms.some((p) => this.dashStringLooksLikeGovernor(p))) return true;
        }
      } catch (_) {}
      try {
        const rolesRaw = sessionStorage.getItem("selectedRoles");
        const roles = rolesRaw ? JSON.parse(rolesRaw) : [];
        if (Array.isArray(roles) && roles.some((r) => this.dashStringLooksLikeGovernor(this.dashRoleRowLabel(r)))) return true;
      } catch (_) {}
      try {
        const raw = sessionStorage.getItem("userInfo");
        const u = raw ? JSON.parse(raw) : {};
        if (this.dashStringLooksLikeGovernor(u.category || u.Category)) return true;
        const des = String(u.designation || u.positionTitle || u.position_title || "").trim().toLowerCase();
        if (des === "governor" || des === "executive") return true;
      } catch (_) {}
      return false;
    },
    dashStringLooksLikeHeadOfHr(s) {
      const u = String(s || "").trim().toUpperCase();
      return u === "HEAD OF HR" || u.includes("HEAD OF HR");
    },
    dashSessionIsHeadOfHr() {
      try {
        const ctxRaw = sessionStorage.getItem("rbac_context");
        if (ctxRaw) {
          const ctx = JSON.parse(ctxRaw);
          const names = ctx?.role_names;
          if (Array.isArray(names) && names.some((n) => this.dashStringLooksLikeHeadOfHr(n))) return true;
        }
      } catch (_) {}
      try {
        const rbacStr = sessionStorage.getItem("rbac_permissions");
        if (rbacStr) {
          const perms = JSON.parse(rbacStr);
          if (Array.isArray(perms) && perms.some((p) => this.dashStringLooksLikeHeadOfHr(p))) return true;
        }
      } catch (_) {}
      try {
        const rolesRaw = sessionStorage.getItem("selectedRoles");
        const roles = rolesRaw ? JSON.parse(rolesRaw) : [];
        if (Array.isArray(roles) && roles.some((r) => this.dashStringLooksLikeHeadOfHr(this.dashRoleRowLabel(r)))) return true;
      } catch (_) {}
      return false;
    },
    initializeDashboard() {
      this.loadDashboardData();
    },
    
    async loadDashboardData() {
      try {
        this.refreshing = true;
        const data = new FormData();
        data.append("function", "getDashboard");
        data.append("year", String(this.attendanceSummaryYear));
        const res = await execute(data, authUrl);
        if (!res.data.success) {
          console.error('Dashboard API error:', res.data.message);
          return;
        }
        const d = res.data.data || {};
        this.dashboardScope = d.scope || this.dashboardScope;
        if (this.dashSessionIsGovernor() && !this.dashSessionIsHeadOfHr()) {
          this.dashboardScope = {
            ...this.dashboardScope,
            type: "governor",
            departmentId: "",
            departmentName: "",
            departmentScoped: false,
            hideWageBillByDepartment: false
          };
        }
        this.metrics = d.metrics || this.metrics;
        const attSummary = d.attendanceSummary || {};
        this.attendanceData = { chartData: attSummary.chartData, year: attSummary.year };
        this.attendanceSummaryYear = attSummary.year || this.attendanceSummaryYear;
        const todayAtt = d.todayAttendance || {};
        this.todayAttendanceData = { chartData: todayAtt.chartData };
        this.todayAttendanceStats = {
          present: todayAtt.summary?.present ?? 0,
          absent: todayAtt.summary?.absent ?? 0,
          late: 0,
          total: (todayAtt.summary?.present ?? 0) + (todayAtt.summary?.absent ?? 0) + (todayAtt.summary?.onLeave ?? 0)
        };
        this.staffTurnoverData = d.staffTurnover || null;
        this.natureOfExitData = d.natureOfExits || null;
        this.genderComparisonData = d.genderComparison || null;
        this.disabilityDistribution = d.disabilityDistribution || null;
        this.employeeCompositionData = d.employeeComposition || null;
        this.jobGroupsData = d.jobGroups || null;
        this.wageBillByDepartmentData = d.wageBillByDepartment || null;
        this.loadEthnicityDistribution(this.effectiveDepartmentId);
        this.calculateExitSummary();
        this.calculateGenderStats();
        this.calculateDisabilityStats();
        this.$nextTick(() => {
          this.renderAttendanceChart();
          this.renderTodayAttendanceChart();
          if (this.showStaffDistribution) this.renderStaffTurnoverChart();
          this.renderNatureOfExitsChart();
          this.renderEmployeeCompositionChart();
          this.renderJobGroupsChart();
          if (this.showWageBillPerDepartment) this.renderWageBillChart();
          this.renderGenderComparisonChart();
          this.renderDisabilityDistributionChart();
        });
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        this.refreshing = false;
      }
    },
    
    refreshDashboard() {
      this.loadDashboardData();
    },
    
    setupAutoRefresh() {
      this.autoRefreshInterval = setInterval(() => {
        if (document.visibilityState === 'visible') this.loadDashboardData();
      }, 300000);
    },
    
    goToEmployeeRegister() { this.$router.push('/employee-register'); },
    goToHRActions() { this.$router.push('/hr-actions'); },
    goToAttendance() { this.$router.push('/report-monthly'); },
    goToLeaveManagement() { this.$router.push('/attendance-leave'); },
    logoutDashboard() {
      sessionStorage.clear();
      this.$router.push('/');
    },
    async loadEthnicityDistribution(departmentId = '') {
      try {
        const data = new FormData();
        data.append("function", "getEmployees");
        data.append("page", "1");
        data.append("rows_per_page", "5000");
        data.append("status", "Active");
        // PHP getEmployees filters on `department`, not `departmentId` (departmentId is now also accepted server-side)
        if (departmentId) {
          data.append("department", String(departmentId));
          data.append("departmentId", String(departmentId));
        }
        const res = await execute(data, authUrl);
        let list = res?.data?.employees || res?.data?.data?.employees || [];
        const scopeName = String(this.dashboardScope?.departmentName || "").trim().toLowerCase();
        if (this.dashboardScope?.departmentScoped === true && scopeName) {
          list = (Array.isArray(list) ? list : []).filter((emp) => {
            const dn = String(emp?.departmentName || "").trim().toLowerCase();
            return dn === scopeName;
          });
        }
        const counts = {};
        (Array.isArray(list) ? list : []).forEach((emp) => {
          const key = String(
            emp?.ethnicity ||
            emp?.ethnicGroup ||
            emp?.tribe ||
            emp?.community ||
            "Not specified"
          ).trim() || "Not specified";
          counts[key] = (counts[key] || 0) + 1;
        });
        this.ethnicityDistribution = Object.keys(counts)
          .map((k) => ({ name: k, count: counts[k] }))
          .sort((a, b) => b.count - a.count);
      } catch (_) {
        this.ethnicityDistribution = [];
      }
    },
    
    previousPeriod() { console.log('Previous period'); },
    nextPeriod() { console.log('Next period'); },
    
    decreaseAttendanceYear() { this.attendanceSummaryYear--; this.loadDashboardData(); },
    increaseAttendanceYear() { this.attendanceSummaryYear++; this.loadDashboardData(); },
    
    formatNumber(value) { return value ? parseInt(value).toLocaleString() : '0'; },
    formatWageBillKes(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return '0';
      return n.toLocaleString('en-KE', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    },
    
    getEmployeeMetrics() {
      const data = new FormData();
      data.append("function", "getEmployeeMetrics");
      return execute(data, authUrl).then((res) => {
        if (res.data.success) this.metrics = res.data.data.metrics;
      }).catch(console.error);
    },
    
    getAttendanceSummary() {
      const data = new FormData();
      data.append("function", "getAttendanceSummary");
      data.append("year", String(this.attendanceSummaryYear));
      return execute(data, authUrl).then((res) => {
        if (res.data.success) {
          this.attendanceData = res.data.data;
          this.$nextTick(() => this.renderAttendanceChart());
        }
      }).catch(console.error);
    },
    
    getTodayAttendance() {
      const data = new FormData();
      data.append("function", "getTodayAttendance");
      return execute(data, authUrl).then((res) => {
        if (res.data.success) {
          this.todayAttendanceData = res.data.data;
          this.calculateTodayAttendanceStats();
          this.$nextTick(() => this.renderTodayAttendanceChart());
        }
      }).catch(console.error);
    },
    
    refreshTodayAttendance() { this.getTodayAttendance(); },
    
    calculateTodayAttendanceStats() {
      if (!this.todayAttendanceData?.chartData?.series?.[0]?.data) return;
      const data = this.todayAttendanceData.chartData.series[0].data;
      this.todayAttendanceStats = {
        present: data.find(d => d.name === 'Present')?.y || 0,
        absent: data.find(d => d.name === 'Absent')?.y || 0,
        late: data.find(d => d.name === 'Late')?.y || 0,
        total: data.reduce((sum, item) => sum + (item.y || 0), 0)
      };
    },
    
    getStaffTurnover() {
      const data = new FormData();
      data.append("function", "getStaffTurnover");
      return execute(data, authUrl).then((res) => {
        if (res.data.success) {
          this.staffTurnoverData = res.data.data;
          this.$nextTick(() => this.renderStaffTurnoverChart());
        }
      }).catch(console.error);
    },
    
    getNatureOfExits() {
      const data = new FormData();
      data.append("function", "getNatureOfExits");
      return execute(data, authUrl).then((res) => {
        if (res.data.success) {
          this.natureOfExitData = res.data.data;
          this.calculateExitSummary();
          this.$nextTick(() => this.renderNatureOfExitsChart());
        }
      }).catch(console.error);
    },
    
    calculateExitSummary() {
      if (!this.natureOfExitData?.chartData?.series?.[0]?.data) return;
      this.exitSummary = this.natureOfExitData.chartData.series[0].data
        .map(item => ({ name: item.name, value: item.y }))
        .slice(0, 5);
    },
    
    getGenderComparison() {
      const data = new FormData();
      data.append("function", "getGenderComparison");
      return execute(data, authUrl).then((res) => {
        if (res.data.success) {
          this.genderComparisonData = res.data.data;
          this.calculateGenderStats();
          this.$nextTick(() => this.renderGenderComparisonChart());
        }
      }).catch(console.error);
    },
    
    calculateGenderStats() {
      if (!this.genderComparisonData?.chartData?.series?.[0]?.data) return;
      this.genderComparisonData.chartData.series[0].data.forEach(item => {
        const name = item.name.toLowerCase();
        if (name.includes('male') && !name.includes('female')) this.genderStats.male = item.y;
        else if (name.includes('female')) this.genderStats.female = item.y;
        else this.genderStats.other = (this.genderStats.other || 0) + item.y;
      });
    },
    
    getDisabilityDistribution() {
      const data = new FormData();
      data.append("function", "getDisabilityDistribution");
      return execute(data, authUrl).then((res) => {
        if (res.data.success) {
          this.disabilityDistribution = res.data.data;
          this.calculateDisabilityStats();
          this.$nextTick(() => this.renderDisabilityDistributionChart());
        }
      }).catch(console.error);
    },
    
    calculateDisabilityStats() {
      if (!this.disabilityDistribution?.chartData?.series?.[0]?.data) return;
      const data = this.disabilityDistribution.chartData.series[0].data;
      const withDisabilities = data.find(d => d.name === 'With Disabilities')?.y || 0;
      const withoutDisabilities = data.find(d => d.name === 'Without Disabilities')?.y || 0;
      const total = withDisabilities + withoutDisabilities;
      this.disabilityStats = {
        withDisabilities: withDisabilities,
        total: total,
        percentage: total > 0 ? ((withDisabilities / total) * 100).toFixed(1) : 0
      };
    },
    
    renderAttendanceChart() {
      if (!this.$refs.attendanceChart || !this.attendanceData) {
        console.warn('Attendance chart: Missing ref or data');
        return;
      }
      if (this.chartInstances.attendance) this.chartInstances.attendance.destroy();
      
      const chartData = this.attendanceData.chartData;
      if (!chartData || !chartData.series || !Array.isArray(chartData.series)) {
        console.warn('Attendance chart: Invalid chart data structure');
        return;
      }

      this.chartInstances.attendance = Highcharts.chart(this.$refs.attendanceChart, {
        chart: { type: 'column', height: 280, backgroundColor: 'transparent' },
        title: { text: null },
        xAxis: { 
          type: 'category', 
          labels: { rotation: -45, style: { fontSize: '11px', fontWeight: '500' } },
          gridLineWidth: 0
        },
        yAxis: { 
          min: 0, 
          title: { text: 'Percentage (%)', style: { fontWeight: '600' } }, 
          labels: { format: '{value}%' },
          gridLineColor: '#e5e7eb'
        },
        legend: { enabled: false },
        tooltip: { 
          headerFormat: '<b>{point.key}</b><br/>', 
          pointFormat: '{series.name}: <b>{point.y:.1f}%</b>',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e5e7eb',
          borderRadius: 8,
          shadow: true
        },
        plotOptions: { 
          column: { 
            pointPadding: 0.1, 
            borderWidth: 0, 
            borderRadius: 6,
            dataLabels: { 
              enabled: true, 
              format: '{y:.0f}%',
              style: { fontWeight: '600', textOutline: 'none' }
            } 
          } 
        },
        series: chartData.series
      });
    },
    
    renderTodayAttendanceChart() {
      if (!this.$refs.todayAttendanceChart || !this.todayAttendanceData) return;
      if (this.chartInstances.todayAttendance) this.chartInstances.todayAttendance.destroy();
      
      this.chartInstances.todayAttendance = Highcharts.chart(this.$refs.todayAttendanceChart, {
        chart: { type: 'pie', height: 230 },
        title: { text: null },
        plotOptions: { pie: { allowPointSelect: true, dataLabels: { enabled: true, format: '{point.name}: {point.y}' }, showInLegend: true } },
        series: this.todayAttendanceData.chartData?.series || [{ name: 'Attendance', data: [] }]
      });
    },
    
    renderStaffTurnoverChart() {
      if (!this.$refs.turnoverChart || !this.staffTurnoverData) {
        console.warn('Staff distribution chart: Missing ref or data');
        return;
      }
      if (this.chartInstances.turnover) this.chartInstances.turnover.destroy();
      
      const chartData = this.staffTurnoverData.chartData;
      if (!chartData || !chartData.series || !Array.isArray(chartData.series)) {
        console.warn('Staff distribution chart: Invalid chart data structure');
        return;
      }

      this.chartInstances.turnover = Highcharts.chart(this.$refs.turnoverChart, {
        chart: { type: 'bar', height: 280, backgroundColor: 'transparent' },
        title: { text: null },
        xAxis: { 
          categories: chartData.xAxis?.categories || [], 
          title: { text: null },
          labels: { style: { fontWeight: '500' } },
          gridLineWidth: 0
        },
        yAxis: { 
          min: 0, 
          title: { text: 'Employees', style: { fontWeight: '600' } },
          gridLineColor: '#e5e7eb'
        },
        plotOptions: { 
          bar: { 
            dataLabels: { 
              enabled: true,
              style: { fontWeight: '600', textOutline: 'none' }
            },
            borderRadius: 6
          } 
        },
        tooltip: {
          pointFormat: '<b>{point.y}</b> employees',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e5e7eb',
          borderRadius: 8,
          shadow: true
        },
        legend: { enabled: false },
        series: chartData.series
      });
    },
    
    renderNatureOfExitsChart() {
      if (!this.$refs.exitChart || !this.natureOfExitData) return;
      if (this.chartInstances.exit) this.chartInstances.exit.destroy();
      
      this.chartInstances.exit = Highcharts.chart(this.$refs.exitChart, {
        chart: { type: 'pie', height: 230 },
        title: { text: null },
        plotOptions: { pie: { allowPointSelect: true, dataLabels: { enabled: true, format: '{point.name}: {point.y}' }, showInLegend: true } },
        series: this.natureOfExitData.chartData?.series || [{ name: 'Exits', data: [] }]
      });
    },
    
    renderEmployeeCompositionChart() {
      if (!this.$refs.compositionChart || !this.employeeCompositionData) return;
      if (this.chartInstances.composition) this.chartInstances.composition.destroy();
      this.chartInstances.composition = Highcharts.chart(this.$refs.compositionChart, {
        chart: { type: 'pie', height: 230 },
        title: { text: null },
        plotOptions: { pie: { allowPointSelect: true, dataLabels: { enabled: true, format: '{point.name}: {point.y}' }, showInLegend: true } },
        series: this.employeeCompositionData.chartData?.series || [{ name: 'Employees', colorByPoint: true, data: [] }]
      });
    },
    renderJobGroupsChart() {
      if (!this.$refs.jobGroupsChart || !this.jobGroupsData) return;
      if (this.chartInstances.jobGroups) this.chartInstances.jobGroups.destroy();
      this.chartInstances.jobGroups = Highcharts.chart(this.$refs.jobGroupsChart, {
        chart: { type: 'bar', height: 280 },
        title: { text: null },
        xAxis: { categories: this.jobGroupsData.chartData?.xAxis?.categories || [], title: { text: null } },
        yAxis: { min: 0, title: { text: 'Employees' } },
        plotOptions: { bar: { dataLabels: { enabled: true } } },
        legend: { enabled: false },
        series: this.jobGroupsData.chartData?.series || [{ name: 'Employees', data: [] }]
      });
    },
    renderWageBillChart() {
      if (!this.$refs.wageBillChart || !this.wageBillByDepartmentData) return;
      if (this.chartInstances.wageBill) this.chartInstances.wageBill.destroy();
      const series = this.wageBillByDepartmentData.chartData?.series || [{ name: 'Wage bill (KES)', data: [] }];
      this.chartInstances.wageBill = Highcharts.chart(this.$refs.wageBillChart, {
        chart: { type: 'bar', height: 280 },
        title: { text: null },
        xAxis: { categories: this.wageBillByDepartmentData.chartData?.xAxis?.categories || [], title: { text: null } },
        yAxis: { min: 0, title: { text: 'KES' }, labels: { format: '{value:,.0f}' } },
        plotOptions: { bar: { dataLabels: { enabled: true, format: '{y:,.0f}' } } },
        tooltip: { pointFormat: '<b>{point.y:,.2f}</b> KES' },
        legend: { enabled: false },
        series: series
      });
    },
    renderGenderComparisonChart() {
      if (!this.$refs.genderChart || !this.genderComparisonData) return;
      if (this.chartInstances.gender) this.chartInstances.gender.destroy();

      this.chartInstances.gender = Highcharts.chart(this.$refs.genderChart, {
        chart: { type: 'pie', height: 230 },
        title: { text: null },
        plotOptions: { pie: { allowPointSelect: true, dataLabels: { enabled: true, format: '{point.name}: {point.y}' }, showInLegend: true } },
        series: this.genderComparisonData.chartData?.series || [{ name: 'Gender', data: [] }]
      });
    },
    
    renderDisabilityDistributionChart() {
      if (!this.$refs.disabilityChart || !this.disabilityDistribution) return;
      if (this.chartInstances.disability) this.chartInstances.disability.destroy();
      
      this.chartInstances.disability = Highcharts.chart(this.$refs.disabilityChart, {
        chart: { type: 'pie', height: 230 },
        title: { text: null },
        plotOptions: { pie: { allowPointSelect: true, dataLabels: { enabled: true, format: '{point.percentage:.1f}%' } } },
        series: this.disabilityDistribution.chartData?.series || [{ name: 'Disability', data: [] }]
      });
    },
    
    exportChart(chartType) {
      const chart = this.chartInstances[chartType];
      if (chart) chart.exportChart({ type: 'image/png', filename: `${chartType}-chart` });
    },
    
    cleanupCharts() {
      Object.values(this.chartInstances).forEach(chart => chart?.destroy?.());
    },
    
    showHelp() { this.showHelpModal = true; },
    closeHelpModal() { this.showHelpModal = false; }
  }
}
</script>

<style scoped>
/* ===== Modern Dashboard UI ===== */
.analytics-dashboard {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.analytics-dashboard.nav-collapsed .dashboard-content {
  margin-left: 70px;
}

.dashboard-content {
  margin-left: 280px;
  padding: 24px;
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.page-container {
  max-width: 1600px;
  margin: 0 auto;
}

/* Page Header */
.page-header {
  background: #ffffff;
  border-radius: 4px;
  padding: 20px 24px;
  margin-bottom: 24px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
}

.title-block {
  flex: 1;
}

.page-title {
  font-size: 24px;
  font-weight: 500;
  color: #1e293b;
  margin: 0 0 4px 0;
}

.page-subtitle {
  color: #64748b;
  font-size: 14px;
  margin: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.period-selector {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background: #f9fafb;
}

.period-btn {
  background: none;
  border: none;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #6b7280;
}

.period-btn:hover {
  background: #e5e7eb;
  color: #2195d3;
}

.current-period {
  font-size: 13px;
  font-weight: 500;
  color: #1e293b;
  min-width: 90px;
  text-align: center;
}

.btn-refresh {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: #2195d3;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

.btn-refresh:hover:not(:disabled) {
  background: #1e7ab3;
}

.btn-refresh:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.welcome-banner {
  background: #f0f9ff;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 16px 20px;
  border-left: 3px solid #2195d3;
}

.welcome-title {
  font-size: 18px;
  font-weight: 500;
  color: #1e293b;
  margin: 0 0 4px 0;
}

.welcome-date {
  font-size: 13px;
  color: #64748b;
}

/* Metrics Grid */
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.metric-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  align-items: flex-start;
  gap: 18px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.metric-card:hover {
  border-color: #2195d3;
  box-shadow: 0 8px 24px rgba(33, 149, 211, 0.15);
  transform: translateY(-4px);
}

.metric-icon {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #2195d3 0%, #1e88c7 100%);
  border: none;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 28px;
  box-shadow: 0 4px 12px rgba(33, 149, 211, 0.25);
}

.metric-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.metric-label {
  font-size: 13px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  margin-bottom: 6px;
}

.metric-value {
  font-size: 32px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
  margin-bottom: 6px;
}

.metric-trend {
  font-size: 13px;
  color: #2195d3;
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 600;
}

.metric-trend.negative {
  color: #dc2626;
}

.metric-badge {
  font-size: 11px;
  padding: 2px 8px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  color: #4b5563;
  align-self: flex-start;
}

/* Charts Grid */
.charts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.chart-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  min-height: 360px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.chart-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.card-header {
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(to bottom, #fafbfc, #ffffff);
}

.card-title {
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.card-title i {
  color: #2195d3;
  font-size: 20px;
}

.card-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.control-btn {
  width: 28px;
  height: 28px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #6b7280;
  font-size: 16px;
}

.control-btn:hover {
  border-color: #2195d3;
  color: #2195d3;
  background: #f0f9ff;
}

.btn-logout {
  background: #dc2626;
  color: #fff;
  border-color: #b91c1c;
}

.btn-logout:hover:not(:disabled) {
  background: #b91c1c;
  border-color: #991b1b;
}

.year-control {
  display: flex;
  align-items: center;
  gap: 4px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background: #f9fafb;
}

.current-year {
  font-size: 13px;
  font-weight: 500;
  color: #1e293b;
  min-width: 50px;
  text-align: center;
}

.date-badge, .period-badge {
  font-size: 12px;
  color: #4b5563;
  background: #f3f4f6;
  padding: 2px 8px;
  border-radius: 4px;
}

.card-body {
  padding: 16px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

#AttendanceContainer,
#TodayAttendanceContainer,
#NewContainer,
#ExitChart,
#GenderContainer,
#disabilityChart {
  flex: 1;
  min-height: 220px;
}

/* Stats rows and grids */
.stats-row {
  display: flex;
  gap: 16px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
}

.stat-item {
  flex: 1;
  text-align: center;
}

.stat-row-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
}

.stat-label {
  display: block;
  font-size: 11px;
  color: #6b7280;
  margin-bottom: 4px;
}

.stat-value {
  display: block;
  font-size: 18px;
  font-weight: 500;
  color: #1e293b;
}

/* Placeholders */
.chart-placeholder {
  height: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 2px solid #e5e7eb;
  border-top-color: #2195d3;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  margin-bottom: 8px;
}

.spinner.sm {
  width: 20px;
  height: 20px;
}

/* Footer */
.footer {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 16px 20px;
  margin-top: 24px;
}

.footer-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.footer-left p, .footer-right p {
  margin: 0;
  font-size: 13px;
  color: #6b7280;
}

.system-info {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
}

.system-info i {
  color: #2195d3;
}

.powered-by {
  font-style: italic;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.modal-container {
  background: white;
  border-radius: 4px;
  width: 90%;
  max-width: 400px;
  border: 1px solid #e5e7eb;
}

.modal-header {
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  color: #1e293b;
}

.modal-close {
  background: none;
  border: none;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #6b7280;
  font-size: 20px;
}

.modal-close:hover {
  color: #dc2626;
}

.modal-body {
  padding: 20px;
}

/* Responsive */
@media (max-width: 1024px) {
  .dashboard-content {
    margin-left: 70px;
  }
}

@media (max-width: 768px) {
  .dashboard-content {
    margin-left: 0;
    padding: 16px;
  }
  
  .header-row {
    flex-direction: column;
    align-items: stretch;
  }
  
  .metrics-grid {
    grid-template-columns: 1fr;
  }
  
  .charts-grid {
    grid-template-columns: 1fr;
  }
  
  .footer-content {
    flex-direction: column;
    text-align: center;
  }
}
</style>